/* eslint-disable react/jsx-wrap-multilines */
import React, { FC, useCallback, useEffect, useState } from 'react';
import Input from '@salesforce/design-system-react/components/input';
import Button from '@salesforce/design-system-react/components/button';
import { getUSDString } from '../../util/utilityFunctions';
import SnackBar from '../Shared/SnackBar';
import useSnackBar from '../../util/customHooks/useSnackBar';

import './index.scss';
import { MAX_QUANTITY } from '../../util/constants';
import { ResolvedPrice } from '../../interface/ResolvedPrice';

interface Props {
  items: any[];
  setItems: (items: any[]) => void;
  viewOnly: boolean;
  isAssignUser: boolean;
  pricingListConfig?: any;
  handleSave?: () => void;
  configs?: any;
  updatedPrice?: ResolvedPrice[];
  getUoM: (name: string, packContent: string) => string;
}

const PriceLookupTabularDetails: FC<Props> = ({
  items,
  setItems,
  viewOnly,
  isAssignUser,
  pricingListConfig,
  updatedPrice,
  getUoM,
}) => {
  const { open, notification } = useSnackBar();
  const calculateTotal = useCallback((itemsList: any[]) => {
    return (itemsList || []).reduce((acc, curr) => acc + (curr.lineTotal || 0), 0);
  }, []);

  const [totalPrice, setTotalPrice] = useState(() => calculateTotal(items));

  useEffect(() => {
    setTotalPrice(calculateTotal(items));
  }, [items, calculateTotal]);

  const handleQuantityChange = (productId: string, quantity: number): void => {
    const updatedItems = (items || [])
      .map(item => {
        if (item.productId === productId) {
          const contractPrice = item.contractPrice || 0;
          const lineTotal = quantity * contractPrice;

          return {
            ...item,
            quantity: quantity > MAX_QUANTITY ? MAX_QUANTITY : quantity,
            lineTotal,
          };
        }
        return item;
      })
      .filter(item => item.quantity > 0);
    setItems([...updatedItems]);
  };
  const handleRemoveItem = (productId: string): void => {
    const updatedItems = (items || [])
      .map(item => {
        if (item.productId === productId) {
          return {
            ...item,
            quantity: '',
            deleted: true,
          };
        }
        return item;
      })
      .filter(item => item.quantity !== '');

    setItems(updatedItems);
  };

  interface ValueFunctions {
    [key: string]: (lineItem: any) => any;
  }
  const valueFunctions: ValueFunctions = {
    Part: (lineItem: any) => (
      <div
        style={{
          flex: 1,
          textAlign: 'center',
          wordWrap: 'break-word',
          wordBreak: 'break-all',
          minWidth: '3rem',
        }}
      >
        {lineItem.productNumber}
        {' - '}
        {lineItem.productdescription}
      </div>
    ),
    Quantity: (lineItem: any) => (
      <div className="row-control widthAdjust">
        <Input
          disabled={isAssignUser || viewOnly}
          id={`counter-input-${lineItem.productId}`}
          minValue={0}
          maxValue={Number(MAX_QUANTITY)}
          value={lineItem.quantity || 0}
          variant="counter"
          onChange={(event: any, data: { value: string; number: number }): void => {
            handleQuantityChange(lineItem.productId, data.number);
          }}
        />
      </div>
    ),
    Customer: (lineItem: any) => (
      <div style={{ wordBreak: 'break-all', minWidth: '3rem' }}>{lineItem.accountName || '—'}</div>
    ),
    'Contract Name': (lineItem: any) => (
      <div style={{ wordBreak: 'break-all', minWidth: '3rem' }}>{lineItem.contractName || '—'}</div>
    ),
    Discountable: (lineItem: any) => lineItem.discountableFlag || '—',
    'Contract Price': (lineItem: any) => {
      const { contractPrice } = lineItem;
      return contractPrice !== undefined && contractPrice !== null
        ? getUSDString(contractPrice)
        : '—';
    },
    'Target Price': (lineItem: any) => getUSDString(lineItem.targetPrice || 0),
    'Floor Price': (lineItem: any) => getUSDString(lineItem.floorPrice || 0),
    'List Price': (lineItem: any) => getUSDString(lineItem.listPrice || lineItem.productCost || 0),
    'Start Date': (lineItem: any) => lineItem.startDate || '—',
    'End Date': (lineItem: any) => lineItem.endDate || '—',
    'Selling UoM': (lineItem: any) => lineItem.sellingUoM || 'PCE',
    'Actual UoM': (lineItem: any) => lineItem.actualUoM,
    'Line Total': (lineItem: any) => getUSDString(lineItem.lineTotal || 0),
    Action: (lineItem: any) => (
      <div className="vertical-align-middle" style={{ paddingLeft: '8px' }}>
        <Button
          disabled={viewOnly || isAssignUser}
          iconCategory="utility"
          iconName="delete"
          className="custom-icon-button"
          iconVariant="bare"
          variant="icon"
          onClick={(): void => {
            handleRemoveItem(lineItem.productId);
          }}
        />
      </div>
    ),
  };

  return (
    <>
      <SnackBar open={open} notification={notification} />
      {pricingListConfig?.includes('total_price') && (
        <div
          style={{
            fontSize: '14px',
            textAlign: 'end',
            fontWeight: 'bold',
            padding: '2px 30px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'end',
          }}
        >
          <span>Total Price: </span>
          <span>{getUSDString(totalPrice.toString()) || '$0.00'}</span>
        </div>
      )}
      <div className="lookup-table-container">
        <div className="pricing-table-header">
          {Object.keys(valueFunctions).map(header => (
            <div key={header}>{header}</div>
          ))}
        </div>
        {items?.length > 0 ? (
          items.map((lineItem: any) => (
            <div className="pricing-table-row" key={lineItem.productId}>
              {Object.keys(valueFunctions).map(header => (
                <div key={header}>{valueFunctions[header](lineItem)}</div>
              ))}
            </div>
          ))
        ) : (
          <p className="no-products-message">No products added yet.</p>
        )}
      </div>
    </>
  );
};

export default PriceLookupTabularDetails;
